import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const AdminResourceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'blog',
    shortDescription: '',
    tags: '',
    coverImageUrl: '',
    source: 'internal',
    externalUrl: '',
    pdfUrl: '',
    content: '',
    isFeatured: false,
    status: 'draft'
  });

  useEffect(() => {
    if (isEditing) {
      fetchResource();
    }
  }, [id, isEditing]);

  const fetchResource = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/resources`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }

      const resources = await response.json();
      const resource = resources.find(r => r.id === id);
      
      if (!resource) {
        throw new Error('Resource not found');
      }

      setFormData({
        title: resource.title,
        type: resource.type,
        shortDescription: resource.shortDescription,
        tags: resource.tags.join(', '),
        coverImageUrl: resource.coverImageUrl || '',
        source: resource.source,
        externalUrl: resource.externalUrl || '',
        pdfUrl: resource.pdfUrl || '',
        content: resource.content || '',
        isFeatured: resource.isFeatured,
        status: resource.status
      });
    } catch (err) {
      console.error('Error fetching resource:', err);
      toast.error('Failed to load resource');
      navigate('/admin/resources');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.shortDescription.trim()) {
      toast.error('Short description is required');
      return;
    }

    if (formData.shortDescription.trim().length < 10) {
      toast.error('Short description must be at least 10 characters long');
      return;
    }

    if (formData.shortDescription.trim().length > 500) {
      toast.error('Short description must not exceed 500 characters');
      return;
    }

    if (formData.source === 'external-url' && !formData.externalUrl.trim()) {
      toast.error('External URL is required for external resources');
      return;
    }

    if (formData.source === 'pdf' && !formData.pdfUrl.trim()) {
      toast.error('PDF URL is required for PDF resources');
      return;
    }

    if (formData.source === 'pdf' && formData.pdfUrl.includes('github.com') && !formData.pdfUrl.includes('raw.githubusercontent.com')) {
      toast.error('Please use RAW GitHub URL (raw.githubusercontent.com), not regular GitHub link');
      return;
    }

    if (formData.source === 'internal' && !formData.content.trim()) {
      toast.error('Content is required for internal resources');
      return;
    }

    setSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        shortDescription: formData.shortDescription.trim(),
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        coverImageUrl: formData.coverImageUrl.trim() || null,
        source: formData.source,
        externalUrl: formData.externalUrl.trim() || null,
        pdfUrl: formData.pdfUrl.trim() || null,
        content: formData.content.trim() || null,
        isFeatured: formData.isFeatured,
        status: formData.status
      };

      const url = isEditing 
        ? `${BACKEND_URL}/api/admin/resources/${id}`
        : `${BACKEND_URL}/api/admin/resources`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to save resource';
        throw new Error(errorMessage);
      }

      toast.success(`Resource ${isEditing ? 'updated' : 'created'} successfully`);
      navigate('/admin/resources');
    } catch (err) {
      console.error('Error saving resource:', err);
      toast.error(err.message || `Failed to ${isEditing ? 'update' : 'create'} resource`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="ml-3 text-gray-600">Loading resource...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 p-6 lg:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin/resources"
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>
          <h1 className="text-4xl font-bold">
            {isEditing ? 'Edit Resource' : 'Create New Resource'}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Title */}
          <div>
            <label htmlFor="title" className="text-gray-700 mb-2 block text-sm font-medium">
              Title <span className="text-red-400">*</span>
            </label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter resource title"
              className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label htmlFor="type" className="text-gray-700 mb-2 block text-sm font-medium">
              Type <span className="text-red-400">*</span>
            </label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-3000 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="blog">Blog</option>
              <option value="case-study">Case Study</option>
              <option value="rnd">R&D</option>
              <option value="soft-paper">Soft Paper</option>
              <option value="pdf">PDF</option>
            </select>
          </div>

          {/* Short Description */}
          <div>
            <label htmlFor="shortDescription" className="text-gray-700 mb-2 block text-sm font-medium">
              Short Description <span className="text-red-400">*</span>
            </label>
            <Textarea
              id="shortDescription"
              value={formData.shortDescription}
              onChange={(e) => handleChange('shortDescription', e.target.value)}
              placeholder="Brief description (10-500 characters)"
              rows={3}
              className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.shortDescription.length} / 500 characters (minimum 10 characters required)
            </p>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="text-gray-700 mb-2 block text-sm font-medium">
              Tags
            </label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="Enter tags separated by commas (e.g., AI, ML, Research)"
              className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* Cover Image URL */}
          <div>
            <label htmlFor="coverImageUrl" className="text-gray-700 mb-2 block text-sm font-medium">
              Cover Image URL
            </label>
            <Input
              id="coverImageUrl"
              value={formData.coverImageUrl}
              onChange={(e) => handleChange('coverImageUrl', e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* Source */}
          <div>
            <label htmlFor="source" className="text-gray-700 mb-2 block text-sm font-medium">
              Source <span className="text-red-400">*</span>
            </label>
            <select
              id="source"
              value={formData.source}
              onChange={(e) => handleChange('source', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-3000 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="internal">Internal (Markdown Content)</option>
              <option value="external-url">External URL (e.g., Medium)</option>
              <option value="pdf">PDF Document (GitHub URL)</option>
            </select>
          </div>

          {/* Conditional Fields Based on Source */}
          {formData.source === 'external-url' && (
            <div>
              <label htmlFor="externalUrl" className="text-gray-700 mb-2 block text-sm font-medium">
                External URL <span className="text-red-400">*</span>
              </label>
              <Input
                id="externalUrl"
                value={formData.externalUrl}
                onChange={(e) => handleChange('externalUrl', e.target.value)}
                placeholder="https://medium.com/@yourpost"
                className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This link will open in a new tab when users click on it
              </p>
            </div>
          )}

          {formData.source === 'pdf' && (
            <div>
              <label htmlFor="pdfUrl" className="text-gray-700 mb-2 block text-sm font-medium">
                PDF URL <span className="text-red-400">*</span>
              </label>
              <Input
                id="pdfUrl"
                value={formData.pdfUrl}
                onChange={(e) => handleChange('pdfUrl', e.target.value)}
                placeholder="https://raw.githubusercontent.com/username/repo/main/document.pdf"
                className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                ⚠️ Use RAW GitHub URL (raw.githubusercontent.com), not regular GitHub link. PDF will be displayed in a scrollable viewer on your website.
              </p>
              <p className="text-xs text-yellow-400/70 mt-1">
                Example: https://raw.githubusercontent.com/username/repo/main/file.pdf
              </p>
            </div>
          )}

          {formData.source === 'internal' && (
            <div>
              <label htmlFor="content" className="text-gray-700 mb-2 block text-sm font-medium">
                Content (Markdown) <span className="text-red-400">*</span>
              </label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Write your content in Markdown format...

# Heading 1
## Heading 2

**Bold text** and *italic text*

- List item 1
- List item 2

[Link text](https://example.com)"
                rows={20}
                className="bg-white border-gray-3000 text-gray-900 placeholder:text-gray-400 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports Markdown formatting. Content will be rendered with proper formatting.
              </p>
            </div>
          )}

          {/* Featured */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isFeatured"
              checked={formData.isFeatured}
              onChange={(e) => handleChange('isFeatured', e.target.checked)}
              className="w-4 h-4 rounded border-gray-3000 bg-white text-blue-500 focus:ring-blue-500"
            />
            <label htmlFor="isFeatured" className="text-gray-700 cursor-pointer text-sm font-medium">
              Mark as featured
            </label>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="text-gray-700 mb-2 block text-sm font-medium">
              Status <span className="text-red-400">*</span>
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-3000 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center gap-4 pt-6 border-t border-gray-30000">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Resource' : 'Create Resource'}
                </>
              )}
            </Button>
            <Link to="/admin/resources">
              <Button
                type="button"
                variant="outline"
                className="bg-white hover:bg-gray-100 text-gray-900 border-gray-3000"
              >
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminResourceForm;
